import { useState, useEffect, useMemo, useRef } from "react";
import { Auth } from "shared/@auth/Auth";
import { isEqual, uniqWith, sortBy } from "lodash-es";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMethods } from "react-use";
import createNotification from "shared/@utils/createNotification";

const initialState = {
  id: null,
  title: "",
  subTitle: "",
  description: "",
  status: 1,
  thumbnail: null,
  publishOn: new Date(),
  unPublishOn: null,
  pinLevelPermissions: [1, 2, 4, 8],
  membershipPermissions: [4],
  categories: [],
  countries: [],
  languages: [],
  occupations: [],
  files: [],
  diamonds: [],
  tags: [],
  authors: [],
  familyReunionDiamondGroups: [],
  ownerIBONumber: null,
  additionalIBOs: [],
  applicationSortOrder: [],
  applications: [],
  iboCanPreview: true,
  iboCanShare: false,
};

/**
 * Adds a value to a list if it's not there, removes it if it is.
 * Force add with toggleTo = true and force remove with toggleTo = false
 */
function toggleListItem(list, value, toggleTo = null) {
  const hasItem = list.find((item) => isEqual(value, item));

  if (toggleTo === true || (!hasItem && toggleTo !== false)) {
    return uniqWith([...list, value], isEqual);
  } else {
    return list.filter((item) => !isEqual(item, value));
  }
}

/**
 * Reducer methods that return a new state.
 */
function createMethods(state) {
  return {
    set(newState) {
      return newState;
    },

    reset() {
      return initialState;
    },

    setTitle(title) {
      return { ...state, title };
    },

    setSubTitle(subTitle) {
      return { ...state, subTitle };
    },

    setDescription(description) {
      return { ...state, description };
    },

    setPublishStatus(status) {
      return { ...state, status };
    },

    setPublishOn(publishOn) {
      return { ...state, publishOn };
    },

    setUnPublishOn(unPublishOn) {
      return { ...state, unPublishOn };
    },

    setIBOCanPreview(iboCanPreview) {
      return { ...state, iboCanPreview };
    },

    setIBOCanShare(iboCanShare) {
      return { ...state, iboCanShare };
    },

    setOwnerIBONumber(ownerIBONumber) {
      return { ...state, ownerIBONumber };
    },

    setTags(tags) {
      return { ...state, tags };
    },

    togglePinLevelPermission(value, toggleTo) {
      return {
        ...state,
        pinLevelPermissions: toggleListItem(
          state.pinLevelPermissions,
          value,
          toggleTo
        ).sort(),
      };
    },

    toggleMembershipPermission(value, toggleTo) {
      return {
        ...state,
        membershipPermissions: toggleListItem(
          state.membershipPermissions,
          value,
          toggleTo
        ).sort(),
      };
    },

    toggleFamilyReunionDiamondGroup(id, toggleTo) {
      return {
        ...state,
        familyReunionDiamondGroups: toggleListItem(
          state.familyReunionDiamondGroups,
          id,
          toggleTo
        ).sort(),
      };
    },

    toggleDiamond(id, toggleTo) {
      return {
        ...state,
        diamonds: toggleListItem(state.diamonds, id, toggleTo).sort(),
      };
    },

    toggleAdditionalIBO(iboData, toggleTo) {
      return {
        ...state,
        additionalIBOs: sortBy(
          toggleListItem(state.additionalIBOs, iboData, toggleTo),
          "label"
        ),
      };
    },

    toggleApp(id, toggleTo) {
      return {
        ...state,
        applications: toggleListItem(state.applications, id, toggleTo),
      };
    },

    toggleCategory(id, toggleTo) {
      return {
        ...state,
        categories: toggleListItem(state.categories, id, toggleTo),
      };
    },

    toggleCountry(id, toggleTo) {
      return {
        ...state,
        countries: toggleListItem(state.countries, id, toggleTo),
      };
    },

    toggleLanguage(id, toggleTo) {
      return {
        ...state,
        languages: toggleListItem(state.languages, id, toggleTo),
      };
    },

    toggleOccupation(id, toggleTo) {
      return {
        ...state,
        occupations: toggleListItem(state.occupations, id, toggleTo),
      };
    },
  };
}

export function useContentEdit({ id, optimisticStatus, isSaving = false }) {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();
  const [original, setOriginal] = useState(initialState);
  const [content, methods] = useMethods(createMethods, initialState);
  const optimisticStatusRef = useRef(optimisticStatus);
  const initializedContentIdRef = useRef(null);
  const detailQueryKey = ["cms", "content", id == null ? id : String(id)];

  // Stage thumbnail for upload after main save
  const [thumbData, setThumbData] = useState(null);

  // Stage file for upload after main save
  const [fileData, setFileData] = useState(null);

  // files to be archived
  const [fileIdsToArchive, setFileIdsToArchive] = useState([]);

  // files to be unarchived
  const [fileIdsToUnArchive, setFileIdsToUnArchive] = useState([]);

  // files to be deleted
  const [fileIdsToDelete, setFileIdsToDelete] = useState([]);

  const isChanged = useMemo(
    () =>
      thumbData ||
      fileData ||
      !isEqual(original, content) ||
      fileIdsToArchive.length > 0 ||
      fileIdsToUnArchive.length > 0 ||
      fileIdsToDelete.length > 0,
    [
      original,
      content,
      thumbData,
      fileData,
      fileIdsToArchive,
      fileIdsToUnArchive,
      fileIdsToDelete,
    ]
  );

  // Fetch CMS data for the current content id.
  const { isLoading, isError, data, error } = useQuery(
    detailQueryKey,
    async () => {
      if (id == null) {
        return null;
      }

      return makeAuthedRequest(`/cms/content/${id}`).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      });
    }
  );

  // Keep refetches from replacing unsaved edits or changing the draft mid-save.
  useEffect(() => {
    const isNewContent =
      data != null &&
      String(initializedContentIdRef.current) !== String(data.id);

    if (data != null && (isNewContent || (!isChanged && !isSaving))) {
      const loadedContent = JSON.parse(JSON.stringify(data));
      if (optimisticStatusRef.current != null) {
        loadedContent.status = optimisticStatusRef.current;
        if (data.status === optimisticStatusRef.current) {
          optimisticStatusRef.current = null;
        }
      }
      methods.set(loadedContent);
      setOriginal(JSON.parse(JSON.stringify(loadedContent)));
      initializedContentIdRef.current = data.id;
    }
  }, [data, isChanged, isSaving]);

  const createContentMutation = useMutation(
    () =>
      makeAuthedRequest(`/cms/content`, {
        method: "post",
        body: JSON.stringify(content),
      })
        .then(async (res) => {
          const json = await res.json();

          if (res.ok) {
            return json.contentId;
          } else {
            throw new Error(json.message);
          }
        })
        .then(async (contentId) => {
          if (thumbData) {
            await updateThumbMutation.mutateAsync({
              data: thumbData,
              contentId,
            });
          }
          if (fileData) {
            await uploadFileMutation.mutateAsync({
              data: fileData,
              contentId,
            });
          }

          return contentId;
        }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["cms", "all"]);
        createNotification({
          message: "Content Saved",
          autoDismiss: true,
          type: "success",
        });
      },
    }
  );

  const updateContentMutation = useMutation(
    () =>
      makeAuthedRequest(`/cms/content/${content.id}`, {
        method: "put",
        body: JSON.stringify(content),
      }).then(async (res) => {
        if (!res.ok) {
          const errorBody = await res.json().catch(() => null);
          throw new Error(
            errorBody?.error?.message ||
              errorBody?.message ||
              `Failed to save content (${res.status})`
          );
        }
        let thumbnail = content.thumbnail;
        const savedFileData = fileData;
        if (thumbData) {
          const thumbnailResult = await updateThumbMutation.mutateAsync({
            data: thumbData,
            contentId: content.id,
          });
          thumbnail = thumbnailResult?.imagePath || thumbnail;
        }
        if (savedFileData) {
          await uploadFileMutation.mutateAsync({
            data: savedFileData,
            contentId: content.id,
          });
        }
        return { thumbnail, fileData: savedFileData };
      }),
    {
      onSuccess: async ({ thumbnail, fileData: savedFileData }) => {
        const queryKey = ["cms", "all"];

        await Promise.all([
          queryClient.cancelQueries(queryKey),
          queryClient.cancelQueries(detailQueryKey),
        ]);

        queryClient.setQueryData(detailQueryKey, (currentContent) => ({
          ...(currentContent || {}),
          ...content,
          thumbnail,
          files: currentContent?.files || content.files,
        }));

        const allData = queryClient.getQueryData(queryKey);
        if (Array.isArray(allData?.content)) {
          const tagOptions = [
            ...(allData.settings?.tags?.tags || []),
            ...(allData.settings?.tags?.hiddenTags || []),
            ...(allData.settings?.tags?.internalTags || []),
          ];
          const activeFiles = content.files.filter(
            (file) => !file.archived && !file.deleted
          );
          const fileFormats = savedFileData
            ? [savedFileData.fileFormat]
            : activeFiles.map((file) => file.fileFormat);

          queryClient.setQueryData(queryKey, {
            ...allData,
            content: allData.content.map((item) =>
              item.id === content.id
                ? {
                    ...item,
                    title: content.title,
                    subTitle: content.subTitle,
                    description: content.description,
                    thumbnail,
                    publishOn: content.publishOn,
                    status: content.status,
                    pinLevelPermission: content.pinLevelPermissions.reduce(
                      (flags, permission) => flags | Number(permission),
                      0
                    ),
                    previewPermissions: content.iboCanPreview,
                    sharePermissions: content.iboCanShare,
                    applications: content.applications,
                    tags: content.tags
                      .map(
                        (tagId) =>
                          tagOptions.find((tag) => tag.id === tagId) ||
                          item.tags.find((tag) => tag.id === tagId)
                      )
                      .filter(Boolean),
                    fileFormat: fileFormats,
                    filePath:
                      savedFileData?.path ||
                      savedFileData?.file?.name ||
                      activeFiles[0]?.filePath ||
                      item.filePath,
                  }
                : item
            ),
          });
        }

        await Promise.all([
          queryClient.invalidateQueries(detailQueryKey),
          queryClient.invalidateQueries(queryKey),
        ]);
        setOriginal(content); // Reset original ref to mark as no changes
        createNotification({
          message: "Content Saved",
          autoDismiss: true,
          type: "success",
        });
      },
    }
  );

  const updateThumbMutation = useMutation(
    async ({ data, contentId }) => {
      const body = new FormData();

      body.append("file", data);

      return makeAuthedRequest(`/cms/content/${contentId}/thumbnail`, {
        method: "PUT",
        headers: {
          "Content-Type": undefined,
        },
        body,
      }).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const err = await res.json();
          throw new Error(err.message);
        }
      });
    },
    {
      onSuccess: () => {
        setThumbData(null);
        queryClient.invalidateQueries(["cms", "all"]);
        queryClient.invalidateQueries(["cms", "content"]);
      },
    }
  );

  const uploadFileMutation = useMutation(
    async ({ data, contentId }) => {
      if (data == null) {
        console.warn(`Trying to upload file with null data`);
        return;
      }

      let contentType;
      let body;

      if (data.file) {
        contentType = undefined;
        body = new FormData();
        body.append("file", data.file);
        body.append("version", data.version);
        body.append("fileFormat", data.fileFormat);
      } else {
        contentType = "application/json";
        body = JSON.stringify({
          path: data.path,
          version: data.version,
          fileFormat: data.fileFormat,
        });
      }

      return makeAuthedRequest(`/cms/content/${contentId}/files`, {
        method: "POST",
        headers: {
          "Content-Type": contentType,
        },
        body,
      });
    },
    {
      onSuccess: () => {
        setFileData(null);
        queryClient.invalidateQueries(["cms", "content"]);
      },
    }
  );

  const archiveFileMutation = useMutation(
    async ({ fileId, contentId }) => {
      return makeAuthedRequest(`/cms/content/${contentId}/files/${fileId}`, {
        method: "PUT",
        body: JSON.stringify({
          archived: true,
        }),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["cms", "content"]);
      },
    }
  );

  const unarchiveFileMutation = useMutation(
    async ({ fileId, contentId }) => {
      return makeAuthedRequest(`/cms/content/${contentId}/files/${fileId}`, {
        method: "PUT",
        body: JSON.stringify({
          archived: false,
        }),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["cms", "content"]);
      },
    }
  );

  const deleteFileMutation = useMutation(
    async ({ fileId, contentId }) => {
      return makeAuthedRequest(`/cms/content/${contentId}/files/${fileId}`, {
        method: "PUT",
        body: JSON.stringify({
          deleted: true,
        }),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["cms", "content"]);
      },
    }
  );

  return {
    isLoading:
      isLoading || (id != null && data != null && content.id !== data.id),
    isError,
    isChanged,
    content,
    methods,
    error,

    original,

    thumbData,
    setThumbData,
    fileData,
    setFileData,

    createContentMutation,
    updateContentMutation,
    archiveFileMutation,
    unarchiveFileMutation,
    deleteFileMutation,

    fileIdsToArchive,
    setFileIdsToArchive,
    fileIdsToUnArchive,
    setFileIdsToUnArchive,
    fileIdsToDelete,
    setFileIdsToDelete,
  };
}
